// PatientNotificationPopup.jsx
import { useState } from "react";
import axios from 'axios';
import Swal from 'sweetalert2';

// Suggestions grouped by category
const defaultSuggestions = {
    Workup: [
        "Please proceed to Workup for your preliminary eye tests; kindly follow the technician's instructions."
    ],
    OCT: [
        "Please proceed to the OCT room for your retinal imaging."
    ],
    Exam: [
        "Kindly go to Exam Room for your consultation with the doctor."
    ],
    Reports: [
        "Please collect your reports from the front desk.",
        "Your lab results are ready; kindly pick them up at the front desk.",
        "Please stop by the reception to complete any paperwork."
    ],
    Waiting: [
        "The doctor is ready for you in Exam Room.",
        "Your appointment is ready; please proceed to the waiting area.",
        "Kindly wait in the waiting room; the doctor will call you shortly."
    ],
    Instructions: [
        "Please bring your insurance card and photo ID.",
        "Kindly bring your current medication list.",
        "Do not drive immediately after dilation.",
        "Eye drops have been administered; please wait 10 minutes before your exam.",
        "Please complete your electronic paperwork before seeing the doctor.",
        "Remember to bring any previous eye reports or prescriptions.",
        "Please be prepared to pay your copay at the front desk.",
        "Bring appropriate payment for any services not covered by insurance.",
    ]
};

export default function PatientNotificationPopup({
    patient,
    suggestions = defaultSuggestions,
    config,
    onClose
}) {
    const [message, setMessage] = useState("");
    const [activeCategory, setActiveCategory] = useState("Workup");

    const hasChart = !!(patient?.chartNumber);

    const handleSuggestionClick = (text) => {
        setMessage(text);
    };

    const canSend = message.trim().length > 0 && hasChart;

    const handleSend = async () => {
        if (!canSend) return;
        const notificationResponse = await axios.post(
            `${config.API_BASE_URL}/notifications/notify_patient`,
            {
                message: message.trim(),
                chart: patient.chartNumber
            },
            {
                headers: {
                    "action_id": config.ACTIONID['edit'], // Assuming the action_id for 'view' is set in config
                    "Content-Type": "application/json",
                    "Authorization": `${localStorage.getItem("digital-form-idtoken")}`,
                    "practice": localStorage.getItem("digital-form-dept-practice")
                },
            }
        );
        const response = notificationResponse.text
        if (notificationResponse.status === 200) {
            // swal alert for success
            Swal.fire({
                title: 'Notification Sent!',
                text: "Your message has been sent to the patient.",
                icon: 'success'
            }).then(() => {
                onClose();
            });
        }
        else {
            const errorMessage = response ? response : "An unknown error occurred.";
            Swal.fire({
                title: 'Error',
                text: `There was an issue sending the notification. Please try again. ${errorMessage}`,
                icon: 'error'
            });
        }

        setMessage("");
    };

    const categories = Object.keys(suggestions);

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
            <div className="bg-white p-6 rounded-xl w-full max-w-md md:max-w-lg shadow-lg">
                <h2 className="text-2xl font-semibold mb-4 text-gray-800">
                    Send Notification to {patient?.name || "Patient"}
                </h2>

                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                        Message
                    </label>
                    <textarea
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type your message or select a suggestion..."
                        className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                    />
                </div>

                {/* Category Tabs */}
                <div className="mb-2 flex gap-2 flex-wrap">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            type="button"
                            onClick={() => setActiveCategory(cat)}
                            className={`px-3 py-1 rounded-full font-medium text-sm ${activeCategory === cat
                                ? "bg-blue-600 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                } transition duration-150`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* Suggestions for Active Category */}
                <div className="mb-4 flex flex-wrap gap-2">
                    {suggestions[activeCategory].map((s, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => handleSuggestionClick(s)}
                            className="bg-blue-600 text-white text-sm px-3 py-1 rounded-full hover:bg-blue-700 transition duration-150 shadow-sm"
                        >
                            {s}
                        </button>
                    ))}
                </div>

                {!hasChart && (
                    <p className="text-red-600 text-sm mb-2">
                        Cannot send notification: patient has no chart.
                    </p>
                )}

                <div className="flex justify-end gap-2">
                    <button
                        onClick={handleSend}
                        disabled={!canSend}
                        className={`px-4 py-2 rounded text-white font-medium ${canSend
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-gray-300 cursor-not-allowed"
                            } transition duration-150`}
                    >
                        Send
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition duration-150"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}